import { useCallback, useMemo } from 'react'

import { MeetingTypeWithId } from 'types/MeetingType'

import useCustomMeetingTypes from './useCustomMeetingTypes'
import useOrgMeetingTypes from './useOrgMeetingTypes'

export default function useMeetingTypesList() {
  const customMeetingTypesData = useCustomMeetingTypes()
  const orgMeetingTypesData = useOrgMeetingTypes()

  const meetingTypesById = useMemo(
    () => ({
      ...customMeetingTypesData.meetingTypes,
      ...orgMeetingTypesData.meetingTypes,
    }),
    [customMeetingTypesData.meetingTypes, orgMeetingTypesData.meetingTypes]
  )

  const meetingTypes: MeetingTypeWithId[] = useMemo(
    () =>
      Object.entries(meetingTypesById)
        .map(([id, value]) => ({ id, ...value }))
        .sort((a, b) => {
          if (a.default || b.default) {
            return a.default ? -1 : 1
          }

          if (a.orgMeeting || b.orgMeeting) {
            return a.orgMeeting ? -1 : 1
          }

          return a.name.localeCompare(b.name)
        }),
    [meetingTypesById]
  )

  const isLoading =
    !customMeetingTypesData.meetingTypes || !orgMeetingTypesData.meetingTypes

  const isError = customMeetingTypesData.isError || orgMeetingTypesData.isError

  const onDeleteMeetingType = useCallback(
    (meeting: MeetingTypeWithId) => {
      meeting.orgMeeting
        ? orgMeetingTypesData.onDelete(meeting)
        : customMeetingTypesData.onDelete(meeting)
    },
    [customMeetingTypesData, orgMeetingTypesData]
  )

  return {
    meetingTypes: meetingTypes,
    meetingTypesById: meetingTypesById,
    isLoading: isLoading || isError,
    isError: isError,
    onDeleteMeetingType,
  }
}
